import { useEffect } from 'react';
import { useStore } from '../../store';

type ChatHistoryState = {
  chat?: boolean;
  chatConversation?: boolean;
  chatLightbox?: boolean;
  chatSubview?: boolean;
} | null;

const historyState = () => window.history.state as ChatHistoryState;

/** Module-level, so a StrictMode re-mount cancels the pop its cleanup queued. */
let pendingSheetBack: ReturnType<typeof setTimeout> | undefined;
let pendingConversationBack: ReturnType<typeof setTimeout> | undefined;
/** True from the sheet effect's cleanup until it runs again. */
let sheetTornDown = false;
/** True from the conversation effect's cleanup until it runs again. */
let conversationTornDown = false;
let pendingSubviewBack: ReturnType<typeof setTimeout> | undefined;

/** True while the sheet is torn down; the lightbox leaves its entry to it. */
export const sheetIsTornDown = () => sheetTornDown;

/** The sheet shows a conversation (or New message), not its inbox. */
const inConversation = (state: ReturnType<typeof useStore.getState>) =>
  state.chatSheetView === 'conversation' && state.chatWindows.open.length > 0;

/** The conversation shows Members or Announcers instead of its messages. */
const inSubview = (state: ReturnType<typeof useStore.getState>) => {
  const id = state.chatWindows.open[0];
  return (
    inConversation(state) &&
    id !== undefined &&
    (state.windowViews[id] ?? 'conversation') !== 'conversation'
  );
};

/**
 * Below 1024 px the sheet covers the screen, so the back button must close it
 * rather than leave the page. Opening the sheet pushes `{chat}`; opening a
 * conversation in it pushes `{chat, chatConversation}`. Back from a
 * conversation returns to the inbox, Back from the inbox closes the sheet.
 * Members and Announcers push `{chat, chatConversation, chatSubview}`: Back
 * from there returns to the conversation.
 */
export function useMobileBackClose(): void {
  // Only the sheet covers the screen; the tray floats beside the chart.
  const sheetOpen = useStore(
    state => state.chatLayout === 'sheet' && state.chatSheetOpen
  );
  const conversationOpen = useStore(inConversation);
  const subviewOpen = useStore(inSubview);
  const setChatSheetOpen = useStore(state => state.setChatSheetOpen);
  const setChatSheetView = useStore(state => state.setChatSheetView);

  useEffect(() => {
    if (!sheetOpen) return;
    clearTimeout(pendingSheetBack);
    sheetTornDown = false;
    // A StrictMode re-run finds its own entry on top and keeps it.
    if (!historyState()?.chat) window.history.pushState({ chat: true }, '');
    const onPopState = (event: PopStateEvent) => {
      const state = event.state as ChatHistoryState;
      // Back from a layer above the conversation (the attachment lightbox)
      // lands on the conversation's own entry: that layer closed, the chat
      // stays (EMR-1878). A traversal that lands on a lightbox entry (Forward)
      // is not a Back out of the chat either.
      if (state?.chatLightbox || state?.chatConversation) {
        // Back from Members or Announcers lands on the conversation's entry.
        const current = useStore.getState();
        const id = current.chatWindows.open[0];
        if (!state.chatSubview && id && inSubview(current))
          current.setChatWindowView(id, 'conversation');
        return;
      }
      if (state?.chat) {
        // Back from a conversation lands on the sheet's own entry.
        if (inConversation(useStore.getState())) setChatSheetView('inbox');
        return;
      }
      setChatSheetOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      sheetTornDown = true;
      // Closed by a control, not by Back: remove the sheet's entries, or the
      // next Back after a reopen lands on them and is ignored. Deferred, so a
      // StrictMode re-mount can cancel it.
      pendingSheetBack = setTimeout(() => {
        const state = historyState();
        // The lightbox entry sits on top of the conversation's: drop all three.
        if (state?.chatLightbox) window.history.go(-3);
        else if (state?.chatSubview) window.history.go(-3);
        else if (state?.chatConversation) window.history.go(-2);
        else if (state?.chat) window.history.back();
      });
    };
  }, [sheetOpen, setChatSheetOpen, setChatSheetView]);

  useEffect(() => {
    if (!sheetOpen || !conversationOpen) return;
    clearTimeout(pendingConversationBack);
    conversationTornDown = false;
    if (!historyState()?.chatConversation)
      window.history.pushState({ chat: true, chatConversation: true }, '');
    return () => {
      // Left by a control (the Back button, a gone conversation) while the
      // sheet stays open: drop the conversation's entry. When the sheet's own
      // effect tears down in the same commit (a close, an unmount), its cleanup
      // removes both entries, and a second pop would leave the page.
      conversationTornDown = true;
      pendingConversationBack = setTimeout(() => {
        if (sheetTornDown) return;
        const state = historyState();
        // A subview entry sits on top of the conversation's: drop both.
        if (state?.chatSubview) window.history.go(-2);
        else if (state?.chatConversation) window.history.back();
      });
    };
  }, [sheetOpen, conversationOpen]);

  useEffect(() => {
    if (!sheetOpen || !conversationOpen || !subviewOpen) return;
    clearTimeout(pendingSubviewBack);
    if (!historyState()?.chatSubview)
      window.history.pushState(
        { chat: true, chatConversation: true, chatSubview: true },
        ''
      );
    return () => {
      // The view's own Back (or a gone conversation) left Members or
      // Announcers. A sheet or conversation teardown removes this entry too.
      pendingSubviewBack = setTimeout(() => {
        if (
          !sheetTornDown &&
          !conversationTornDown &&
          historyState()?.chatSubview
        )
          window.history.back();
      });
    };
  }, [sheetOpen, conversationOpen, subviewOpen]);
}
